import React, { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Search, X, Pill, AlertTriangle, ShieldCheck, Loader2, Plus, Info } from "lucide-react";
import { toast } from "sonner";

/**
 * Prescriber strutturato per Terapia:
 * 1) Cerca farmaco (principio, brand, categoria, ATC)
 * 2) Mostra scheda con posologia di default da RCP, controindicazioni, indicazioni, fonte
 * 3) Editor posologia (route, frequenza/die, giorni, dose, istruzioni)
 * 4) Conferma → onPick(item) restituisce voce strutturata pronta per terapia_items
 *
 * Nessuna prescrizione automatica: il medico conferma esplicitamente.
 */
export default function DrugPrescriber({ open, onClose, onPick }) {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  // posology editor state (popolato da selected.default_posology)
  const [route, setRoute] = useState("");
  const [dose, setDose] = useState("");
  const [freq, setFreq] = useState("");
  const [days, setDays] = useState("");
  const [instructions, setInstructions] = useState("");
  const [extraNotes, setExtraNotes] = useState("");

  // load categories once
  useEffect(() => {
    if (!open) return;
    (async () => {
      try { const { data } = await api.get("/medico/drugs/categories"); setCategories(data || []); } catch {}
    })();
  }, [open]);

  // search
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = { limit: 30 };
        if (q.trim()) params.q = q.trim();
        if (category) params.category = category;
        if (verifiedOnly) params.verified_only = true;
        const { data } = await api.get("/medico/drugs", { params });
        setItems(data.items || []);
      } catch (e) { toast.error(apiErr(e, "Errore ricerca farmaci")); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, category, verifiedOnly, open]);

  const reset = () => {
    setSelected(null); setRoute(""); setDose(""); setFreq(""); setDays(""); setInstructions(""); setExtraNotes("");
  };

  const select = (d) => {
    setSelected(d);
    const p = d.default_posology || {};
    setRoute(p.route || "");
    setDose(p.dose || "");
    setFreq(p.frequency_per_day != null ? String(p.frequency_per_day) : "");
    setDays(p.days != null ? String(p.days) : "");
    setInstructions(p.instructions || "");
    setExtraNotes("");
  };

  const confirm = () => {
    if (!selected) return;
    const item = {
      drug_atc: selected.atc,
      principle: selected.principle,
      brand: selected.brand,
      category: selected.category,
      form: selected.form,
      route: route || (selected.default_posology?.route || ""),
      dose: dose || "",
      frequency_per_day: freq ? Number(freq) : null,
      days: days ? Number(days) : null,
      instructions: instructions || "",
      extra_notes: extraNotes || "",
      indications_ref: selected.indications || "",
      contraindications_ref: selected.contraindications || "",
      warnings_ref: selected.warnings || "",
      source: selected.source || "",
      source_url: selected.source_url || null,
      verified: !!selected.verified,
      confirmed_at: new Date().toISOString(),
    };
    onPick && onPick(item);
    toast.success(`${selected.principle} aggiunto alla terapia`);
    reset();
    onClose && onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="drug-prescriber-modal">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-brand-navy text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Pill size={18} />
            <div>
              <div className="font-extrabold text-lg leading-tight">Prontuario · Aggiungi farmaco</div>
              <div className="text-xs text-white/70">Posologie di default da RCP/AIFA. Modificabili dal medico prima della conferma.</div>
            </div>
          </div>
          <button data-testid="drug-prescriber-close" onClick={() => { reset(); onClose && onClose(); }} className="hover:bg-white/10 p-1.5 rounded-lg"><X size={18} /></button>
        </div>

        {/* Body */}
        {!selected ? (
          <div className="p-5 flex-1 overflow-auto">
            <div className="flex gap-2 mb-3">
              <div className="flex-1 relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="drug-search-input"
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Cerca principio attivo, nome commerciale, categoria, ATC…"
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-[#E2E8F0] focus:outline-none focus:border-brand-azure text-sm"
                />
              </div>
              <select
                data-testid="drug-category-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="px-3 py-2.5 rounded-lg border border-[#E2E8F0] text-sm bg-white max-w-[260px]"
              >
                <option value="">Tutte le categorie</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <label className="flex items-center gap-2 text-xs font-bold text-slate-600 px-3 rounded-lg border border-[#E2E8F0] bg-white cursor-pointer">
                <input data-testid="drug-verified-only" type="checkbox" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} />
                Solo verified
              </label>
            </div>

            {loading && <div className="text-sm text-slate-500 py-6 text-center">Caricamento…</div>}
            {!loading && items.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">Nessun farmaco trovato.</div>}
            <ul className="space-y-1.5" data-testid="drug-results-list">
              {items.map((d) => (
                <li key={`${d.atc}-${d.brand}`}>
                  <button
                    data-testid={`drug-row-${d.atc}-${d.brand.replace(/\s+/g,'_')}`}
                    onClick={() => select(d)}
                    className="w-full text-left px-3 py-2.5 rounded-lg border border-[#E2E8F0] hover:border-brand-azure hover:bg-brand-azure/5 transition flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="font-bold text-brand-navy truncate flex items-center gap-2">
                        {d.brand}
                        {d.verified ? (
                          <span className="text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold">RCP</span>
                        ) : (
                          <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">demo</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate">{d.principle} · {d.form}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-wider">{d.category} · ATC {d.atc}</div>
                    </div>
                    <Plus size={14} className="text-brand-azure shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="p-5 flex-1 overflow-auto">
            {/* Header farmaco selezionato */}
            <div className="bg-brand-azure/5 border border-brand-azure/30 rounded-xl p-4 mb-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-slate-500">{selected.category}</div>
                  <div className="font-extrabold text-brand-navy text-lg">{selected.brand}</div>
                  <div className="text-sm text-slate-700"><b>{selected.principle}</b> · {selected.form}</div>
                </div>
                <div className="text-right shrink-0">
                  {selected.verified ? (
                    <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-1 rounded font-bold flex items-center gap-1"><ShieldCheck size={11} /> RCP verified</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-1 rounded font-bold flex items-center gap-1"><AlertTriangle size={11} /> demo da verificare</span>
                  )}
                  <div className="text-[10px] text-slate-500 mt-1">ATC {selected.atc}</div>
                </div>
              </div>
            </div>

            {/* Indicazioni / Controindicazioni */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-800 mb-1 flex items-center gap-1.5"><Info size={11} /> Indicazioni</div>
                <div className="text-xs text-emerald-900 leading-relaxed">{selected.indications || "—"}</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-3" data-testid="drug-contraindications-panel">
                <div className="text-[11px] font-bold uppercase tracking-wider text-red-800 mb-1 flex items-center gap-1.5"><AlertTriangle size={11} /> Controindicazioni principali</div>
                <div className="text-xs text-red-900 leading-relaxed">{selected.contraindications || "—"}</div>
              </div>
            </div>

            {selected.warnings && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-900 leading-relaxed">
                <b className="block text-amber-800 uppercase text-[10px] tracking-wider mb-1">Avvertenze / sicurezza</b>
                {selected.warnings}
              </div>
            )}

            {/* Editor posologia */}
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mb-3">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-2 flex items-center gap-1.5">
                <Pill size={11} /> Posologia proposta da bugiardino/RCP — <span className="text-brand-orange">verificare e adattare al paziente</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500">Via</label>
                  <input data-testid="drug-route" value={route} onChange={(e) => setRoute(e.target.value)} className="mt-1 w-full px-2.5 py-1.5 rounded border border-[#E2E8F0] text-xs" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500">Dose</label>
                  <input data-testid="drug-dose" value={dose} onChange={(e) => setDose(e.target.value)} className="mt-1 w-full px-2.5 py-1.5 rounded border border-[#E2E8F0] text-xs" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500">Volte/die</label>
                  <input data-testid="drug-freq" type="number" min="0" max="24" value={freq} onChange={(e) => setFreq(e.target.value)} className="mt-1 w-full px-2.5 py-1.5 rounded border border-[#E2E8F0] text-xs" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500">Giorni</label>
                  <input data-testid="drug-days" type="number" min="0" max="365" value={days} onChange={(e) => setDays(e.target.value)} placeholder={selected.default_posology?.days == null ? "cronica" : ""} className="mt-1 w-full px-2.5 py-1.5 rounded border border-[#E2E8F0] text-xs" />
                </div>
              </div>
              <div className="mt-2">
                <label className="text-[10px] font-bold uppercase text-slate-500">Istruzioni</label>
                <textarea data-testid="drug-instructions" rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} className="mt-1 w-full px-2.5 py-1.5 rounded border border-[#E2E8F0] text-xs" />
              </div>
              <div className="mt-2">
                <label className="text-[10px] font-bold uppercase text-slate-500">Note personali (opz.)</label>
                <input data-testid="drug-extra-notes" value={extraNotes} onChange={(e) => setExtraNotes(e.target.value)} placeholder="es. valutare risposta a 7gg" className="mt-1 w-full px-2.5 py-1.5 rounded border border-[#E2E8F0] text-xs" />
              </div>
            </div>

            {/* Fonte */}
            <div className="text-[10px] text-slate-500 mb-3 flex items-center gap-1.5 flex-wrap">
              <ShieldCheck size={10} className="inline" />
              <span>Fonte: <b>{selected.source || "—"}</b></span>
              {selected.source_url && <a href={selected.source_url} target="_blank" rel="noreferrer" className="text-brand-azure underline">apri</a>}
              {selected.source_last_updated && <span>· aggiornato {selected.source_last_updated}</span>}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between gap-2">
              <button onClick={reset} className="text-sm text-slate-600 hover:text-brand-navy">← Cambia farmaco</button>
              <div className="flex gap-2">
                <button onClick={() => { reset(); onClose && onClose(); }} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100">Annulla</button>
                <button data-testid="drug-confirm" onClick={confirm} className="bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-5 py-2 rounded-lg text-sm flex items-center gap-2">
                  <Plus size={14} /> Aggiungi alla terapia
                </button>
              </div>
            </div>
            <div className="mt-3 text-[10px] text-slate-400 italic flex items-start gap-1.5">
              <ShieldCheck size={10} className="mt-0.5" />
              Il medico è responsabile della conferma finale. L'IA/prontuario propone, non prescrive.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
